import React, { useCallback, useEffect, useRef, useState } from "react";
import { Layers, Moon, RefreshCw, Sun, TriangleAlert } from "lucide-react";

import { dir, money, signed } from "./reportCommon";
import useDevTheme from "./useDevTheme";
import "./InvestorReport.css";
import "./reportBoard.css";

/**
 * /all/investment/overview — EVERY CONTAINER SEPARATELY, ON ONE PAGE.
 *
 * No dropdown, by design: the page exists to show the books side by side, so a
 * selector would be a control whose only use is hiding most of the answer.
 *
 * FOUR FIGURES PER BOOK and five per account, and nothing else. The full report
 * lives at /investor-report; this is the board somebody watches while the market
 * is open, and every figure that is not one of those five is noise on it.
 *
 *   session P&L    the 02:00→02:00 trading day's realised P&L, with its trade
 *                  count. NOT "since the poller connected" — a restart would
 *                  zero a number people read as performance.
 *   total P&L      realised since the current period began — each container's
 *                  own pairStartDate/Time, not a date shared across them — with
 *                  its trade count.
 *   floating P&L   the open positions, marked to the broker's last price.
 *   lots           PER LEG. The pair is hedged, so unequal lots is a fault (a
 *                  leg that did not fill) and writing one total hides exactly
 *                  the anomaly worth seeing.
 *
 * It reads /api/investor-report/all-containers/board, which lists every
 * container this audience may see INCLUDING private ones — asked for
 * explicitly.
 *
 * DISABLED CONTAINERS ARE NOT LISTED, on this page or any other. They used to
 * be, shown with a `disabled` flag, on the reasoning that a board with no
 * combined total has nothing for a switched-off book to corrupt. That is now
 * overridden by the plainer rule: disabling a container takes it off every page
 * for everybody, developer included, and this is a page. `containerScope`
 * enforces it server-side, so nothing here has to filter. The one place a
 * disabled book stays visible is /dev/containers, which is where it is switched
 * back on.
 *
 * POLLED, NOT STREAMED. There is one SSE stream per container and this page
 * shows all of them; opening N streams to save a small request would trade a
 * clear refresh for N reconnect states to explain. The interval is the same
 * order as the poller's own history cadence, so the figures are never more than
 * a few seconds behind what a stream would have delivered.
 */

const BOARD_URL = "/api/investor-report/all-containers/board";
const EVERY_MS = 15000;

/**
 * SHOW THE "ALL CONTAINERS" CARD? Yes.
 *
 * Kept as a flag rather than inlined, because it has already been turned off
 * once and asked back on: the switch is the feature. Nothing else has to move
 * either way — `boardFor` computes and returns the `combined` block whether or
 * not this is true, so turning it off costs a request nothing and turning it on
 * needs no rebuild.
 *
 * WHICH BOOKS IT COUNTS IS NOT THIS FLAG'S BUSINESS. A disabled container joins
 * no total and `includeInOverall` governs everything combined — see the table on
 * ContainerSchema — so boardFor applies that filter and names anything it left
 * out, which the card renders beneath its title.
 */
const SHOW_OVERALL = true;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** "1.00 / 1.00" — per leg, or a dash when nothing is connected. */
function lotsText(lots) {
  if (!Array.isArray(lots) || lots.length === 0) return "—";
  return lots.map((l) => (l == null ? "—" : Number(l).toFixed(2))).join(" / ");
}

/**
 * A figure with its trade count underneath, which is the shape this page is.
 *
 * `known: false` PRINTS A DASH INSTEAD OF ZERO, and that distinction is the
 * whole reason this prop exists. Session P&L and the period total are read from
 * combine's in-memory book, which is filled by a rebuild - and a rebuild only
 * runs when a poll actually writes something. A container nobody is polling, or
 * any container in a process that has just restarted, therefore has an empty
 * book, and every one of the four figures on this page computes to 0.00 from it.
 *
 * "0.00" and "nothing recorded" are not the same claim. On a page whose entire
 * job is those four numbers, printing a confident zero for a book that simply
 * has not been read yet is the worst thing it could do - so it prints nothing
 * and the flag on the card says why.
 */
function CountFigure({ label, value, cls, trades, known = true }) {
  return (
    <div className="ir-fig">
      <span className="ir-fig-label">{label}</span>
      <b className={`ir-fig-value ${known ? cls || "" : "ir-muted"}`}>{known ? value : "—"}</b>
      <em className="ir-fig-sub">
        {!known ? "not read yet" : trades == null ? "—" : `${trades} trade${trades === 1 ? "" : "s"}`}
      </em>
    </div>
  );
}

function BrokerCard({ acc, ccy, known = true }) {
  return (
    <article className="irb-card">
      <header className="irb-card-head">
        <span className="irb-card-badge" aria-hidden="true">
          {(acc.label || "??").slice(0, 2).toUpperCase()}
        </span>
        <div className="irb-card-id">
          <h4>{acc.label || `#${acc.login}`}</h4>
          <p>
            #{acc.login}
            {acc.connected ? "" : " · offline"}
          </p>
        </div>
      </header>

      <div className="ir-comb-figs">
        <div className="ir-fig">
          <span className="ir-fig-label">Balance</span>
          <b className="ir-fig-value">{money(acc.balance, ccy)}</b>
        </div>
        <CountFigure
          label="Session P&L"
          value={signed(acc.sessionPnl, ccy)}
          cls={`ir-${dir(acc.sessionPnl)}`}
          trades={acc.sessionTrades}
          known={known}
        />
        <CountFigure
          label="Total P&L"
          value={signed(acc.closedPnl, ccy)}
          cls={`ir-${dir(acc.closedPnl)}`}
          trades={acc.pairTrades}
          known={known}
        />
        <div className="ir-fig">
          <span className="ir-fig-label">Lots</span>
          <b className="ir-fig-value">{acc.lots == null ? "—" : Number(acc.lots).toFixed(2)}</b>
        </div>
        <div className="ir-fig">
          <span className="ir-fig-label">Floating P&L</span>
          <b className={`ir-fig-value ir-${dir(acc.floating)}`}>{signed(acc.floating, ccy)}</b>
        </div>
      </div>
    </article>
  );
}

export default function ContainerBoard() {
  const { dark, toggle } = useDevTheme();
  const theme = dark ? "dark" : "light";

  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [checkedAt, setCheckedAt] = useState(null);
  const [, tick] = useState(0);
  const timerRef = useRef(null);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setError("");
    try {
      const res = await fetch(BOARD_URL, { headers: authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || `failed (${res.status})`);
      setBoard(body);
      setCheckedAt(Date.now());
      setError("");
    } catch (err) {
      /* A failed silent refresh keeps the figures on screen and says so, rather
         than blanking a board somebody is watching. */
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load({ silent: true }), EVERY_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  /* Re-render once a second so "checked 4s ago" is true rather than frozen at
     whatever it said when the last payload arrived. */
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const cards = (board && board.cards) || [];
  const overall = (board && board.combined) || null;
  const ccy = "USD";
  const agoSec = checkedAt ? Math.round((Date.now() - checkedAt) / 1000) : null;

  return (
    <div className={`ir-root ir-theme-${theme}`} data-ir-theme={theme}>
      <header className="ir-topbar">
        <div className="ir-topbar-inner">
          {/* ONE GROUP, because .ir-topbar-inner is space-between: with the
              logo and the title as separate children it spread them to opposite
              ends of the free space and left a gap the width of the page. The
              report does the same thing - .ir-brand holds its mark AND its text
              - which is why it never had this. */}
          <div className="irb-head">
            <img src="/firstbench-logo.png" alt="" className="ir-brand-logo" />
            <div className="irb-title">
              <h1>All containers — overview</h1>
            <p>
                Session P&amp;L is the 02:00→02:00 trading day. Total P&amp;L is measured from
                each container&apos;s own period start.
              </p>
            </div>
          </div>
          <div className="ir-topbar-right">
            {agoSec != null && <span className="irb-checked">checked {agoSec}s ago</span>}
            <button type="button" className="ir-btn ir-btn-icon" onClick={() => load()} aria-label="Refresh">
              <RefreshCw size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ir-btn ir-btn-icon"
              onClick={toggle}
              aria-label={dark ? "Light theme" : "Dark theme"}
            >
              {dark ? <Sun size={15} aria-hidden="true" /> : <Moon size={15} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      <main className="ir-main">
        {error && (
          <p className="irb-error">
            <TriangleAlert size={15} aria-hidden="true" />
            <span>
              {error}
              {cards.length > 0 ? " — the figures below are from the last successful read." : ""}
            </span>
          </p>
        )}

        {!board && !error && <p className="ir-empty">Loading…</p>}
        {board && cards.length === 0 && <p className="ir-empty">No containers to show.</p>}

        {/* ------------------------- OVERALL, ACROSS BOOKS -------------------
            The same four figures the books below show, summed. WHICH books is
            the flags' decision, not this page's: a disabled container is not
            here at all and `includeInOverall` governs everything combined - see
            the table on ContainerSchema. The excluded ones are named underneath,
            because every card is visible right below this and a reader who adds
            them up by hand must not get a different answer with nothing to
            explain it. */}
        {SHOW_OVERALL && overall && (
          <section className="ir-combined irb-overall" aria-labelledby="irb-all">
            <div className="irb-book-head">
              <span className="ir-eyebrow">
                <Layers size={13} aria-hidden="true" /> Overall · {overall.containers} of{" "}
                {cards.length} container{cards.length === 1 ? "" : "s"} ·{" "}
                {overall.connectedCount}/{overall.accountCount} live
              </span>
              <h2 id="irb-all">All containers</h2>
              {(overall.excluded || []).length > 0 && (
                <div className="irb-flags">
                  {overall.excluded.map((x) => (
                    <span className="irb-flag irb-flag-off" key={x.name}>
                      {x.name} — {x.why}, not counted
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="ir-comb-figs">
              <CountFigure
                label="Session P&L"
                value={signed(overall.sessionPnl, ccy)}
                cls={`ir-${dir(overall.sessionPnl)}`}
                trades={overall.sessionTrades}
              />
              <CountFigure
                label="Total P&L"
                value={signed(overall.closedPnl, ccy)}
                cls={`ir-${dir(overall.closedPnl)}`}
                trades={overall.pairTrades}
              />
              <div className="ir-fig">
                <span className="ir-fig-label">Floating P&L</span>
                <b className={`ir-fig-value ir-${dir(overall.floating)}`}>
                  {signed(overall.floating, ccy)}
                </b>
              </div>
              <div className="ir-fig">
                <span className="ir-fig-label">Lots</span>
                <b className="ir-fig-value">{(overall.lots || 0).toFixed(2)}</b>
                <em className="ir-fig-sub">every leg, every book</em>
              </div>
            </div>
          </section>
        )}

        {/* SIDE BY SIDE, not one per row. Each book carries four figures and its
            brokers, which fits comfortably in half the width on a desktop and
            left the other half empty when these were stacked. The grid collapses
            to one column below the breakpoint in reportBoard.css, where two
            would start truncating money figures. */}
        <div className="irb-books">
        {cards.map((card) => (
          <section className="ir-combined irb-book" key={card.id} aria-labelledby={`b-${card.id}`}>
            <div className="irb-book-head">
              <span className="ir-eyebrow">
                <Layers size={13} aria-hidden="true" /> {card.connectedCount}/{card.accountCount} live
              </span>
              <h2 id={`b-${card.id}`}>{card.name}</h2>
              <div className="irb-flags">
                {/* The server no longer sends a disabled book to this page, so
                    this should never render. Kept as the tell rather than
                    deleted: if one ever arrives, the card says so instead of
                    sitting in the list looking like every other book. */}
                {!card.enabled && <span className="irb-flag irb-flag-off">disabled</span>}
                {!card.publicVisible && <span className="irb-flag">not public</span>}
                {card.mode && card.mode !== "live" && <span className="irb-flag">{card.mode}</span>}
                {/* Says when the figures are not live, rather than letting a
                    stale board read as a current one. */}
                {!card.live && <span className="irb-flag irb-flag-off">not polling</span>}
                {card.ready === false && (
                  <span className="irb-flag irb-flag-off">figures not read yet</span>
                )}
                {card.pairStartDate && (
                  <span className="irb-flag irb-flag-quiet">
                    period from {card.pairStartDate}
                    {card.pairStartTime ? ` ${card.pairStartTime}` : ""}
                  </span>
                )}
              </div>
            </div>

            <div className="ir-comb-figs">
              <CountFigure
                label="Session P&L"
                value={signed(card.sessionPnl, ccy)}
                cls={`ir-${dir(card.sessionPnl)}`}
                trades={card.sessionTrades}
                known={card.ready !== false}
              />
              <CountFigure
                label="Total P&L"
                value={signed(card.closedPnl, ccy)}
                cls={`ir-${dir(card.closedPnl)}`}
                trades={card.pairTrades}
                known={card.ready !== false}
              />
              <div className="ir-fig">
                <span className="ir-fig-label">Floating P&L</span>
                <b className={`ir-fig-value ir-${dir(card.floating)}`}>{signed(card.floating, ccy)}</b>
              </div>
              <div className="ir-fig">
                <span className="ir-fig-label">Lots</span>
                <b className="ir-fig-value">{lotsText(card.lots)}</b>
                {/* A KNOWN mismatch only. `legsMatch` is null when the lots are
                    unknown, and claiming the legs disagree about a container
                    nobody is connected to would be a false alarm. */}
                <em className="ir-fig-sub">
                  {card.legsMatch === false ? "legs do not match" : "per leg"}
                </em>
              </div>
            </div>

            <div className="irb-brokers">
              {(card.accounts || []).length === 0 ? (
                <p className="ir-empty">No account is recording in this container.</p>
              ) : (
                (card.accounts || []).map((a) => (
                  <BrokerCard key={a.login} acc={a} ccy={ccy} known={card.ready !== false} />
                ))
              )}
            </div>
          </section>
        ))}
        </div>
      </main>
    </div>
  );
}
